import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import classNames from 'classnames';
import { loadCSS } from 'fg-loadcss';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Icon, List, ListItem } from '@material-ui/core';
import ActivityIcon from '@material-ui/icons/EventNote';
import ListIcon from '@material-ui/icons/List';
import MenuIcon from '@material-ui/icons/Menu';
import DescriptionIcon from '@material-ui/icons/Description';
import PeopleIcon from '@material-ui/icons/People';
import CustomDropdown from '../CustomDropdown/CustomDropdown.js';
import styles from '../../assets/jss/nextjs-material-kit-pro/components/headerLinksStyle.js';
import { msalConfig } from '../../services/msal';
import PleaseWait from './PleaseWait';
import { logoutGoogle } from '../../util/apiWrapper.js';
import Hidden from '@material-ui/core/Hidden';
import { FEATURE_SWITCH, ROLE } from '../../constants/general';
import { MENU } from '../../constants/menus';
import PATH from '../../constants/paths';
import { showFeature } from '../../util/featureSwitchUtil';
import { callApi } from '../../util/apiWrapper';
import StartImpersonationDialog from '../Impersonation/StartImpersonationDialog';
import { appInsights } from '../../services/appInsights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { isScholarshipCoordinator } from '../../util/miscUtil';

const useStyles = makeStyles(styles);

const SignInSignOutItem = (props) => {
  const authStore = props.authStore;
  const seniorStore = props.seniorStore || props.SeniorStore;
  const { inProgress, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const classes = useStyles();

  const handleSignIn = () => {
    // instance.loginRedirect(msalConfig.loginRequest);
    instance.loginRedirect({ ...msalConfig.loginRequest, redirectStartPage: '/' });
    // const account = accounts[0] || null;
    // authStore.handleUserLogin(account);
  };

  const handleSignOut = async () => {
    try {
      seniorStore?.removeFromSession?.();
      window.localStorage.removeItem('SeniorListIndexStore');
      if (authStore?.isImpersonating) {
        try {
          // end impersonation
          const response = await callApi(authStore, '/user/impersonation', 'DELETE');
          authStore.handleImpersonationEnd(response);
        } catch (error) {
          console.error('Error ending impersonation:', error);
          appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
        }
      }

      await logoutGoogle(authStore);
      authStore.setNotAuthorized(false);

      await instance.logoutRedirect({ postLogoutRedirectUri: '/home' });
    } catch (error) {
      // track the error for monitoring purposes
      console.error('Error during sign out:', error);
      appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
    }
  };

  if (isAuthenticated) {
    return (
      <Button
        size='small'
        variant='text'
        className={classes.dropdownLink}
        style={{ marginBottom: '10px' }}
        startIcon={<Icon className={classNames(classes.dropdownIcons, 'fa', 'fa-sign-out-alt')} />}
        onClick={handleSignOut}
      >
        Sign Out
      </Button>
    );
  } else if (inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect) {
    // inProgress check prevents sign-in button from being displayed briefly after returning from a redirect sign-in. Processing the server response takes a render cycle or two
    return (
      <Button
        size='small'
        variant='text'
        className={classes.dropdownLink}
        style={{ marginBottom: '10px' }}
        startIcon={<Icon className={classNames(classes.dropdownIcons, 'fa', 'fa-sign-in-alt')} />}
        onClick={handleSignIn}
      >
        Sign In
      </Button>
    );
  } else {
    return null;
  }
};

const MenuItems = inject(
  'AuthStore',
  'SeniorStore'
)(
  observer((props) => {
    const authStore = props.authStore;
    const seniorStore = props.seniorStore || props.SeniorStore;
    const isAuthenticated = useIsAuthenticated();
    const classes = useStyles();
    const { dropdownHoverColor } = props;
    const [isLoading, setIsLoading] = useState(false);
    const isStudent = authStore && authStore.roles && authStore.roles.includes('STUDENT');
    const isAlumni = authStore && authStore.roles && authStore.roles.includes('ALUMNI');
    const isFileHostingAdminUser = authStore?.roles?.some((role) => [ROLE.ADMIN, ROLE.AFTERCARE_SPECIALIST].includes(role));
    const isAdminUser = authStore?.roles?.some((role) => [ROLE.ADMIN, ROLE.AFTERCARE_SPECIALIST].includes(role));
    const isFHT = authStore?.roles?.some((role) => [ROLE.FHT].includes(role));
    const canImpersonateUser = authStore?.roles?.includes(ROLE.ADMIN);
    const [featureSwitches, setFeatureSwitches] = useState([]);
    const [isImpersonationDialogOpen, setIsImpersonationDialogOpen] = useState(false);
    const history = useHistory();

    useEffect(() => {
      setIsLoading(true);
      loadCSS('https://use.fontawesome.com/releases/v5.12.0/css/all.css', document.querySelector('#font-awesome-css'));
      setIsLoading(false);
    }, []);

    useEffect(() => {
      async function fetchFeatureSwitchList() {
        if (featureSwitches.length === 0) {
          try {
            const fs = await callApi(authStore, 'featureswitch', 'GET');
            const featureSwitchList = fs.result;
            setFeatureSwitches(featureSwitchList);
          } catch (error) {
            console.warn('Error fetching feature switch list:', error);
          }
        }
      }
      fetchFeatureSwitchList();
    }, [authStore, featureSwitches]);

    const clearSeniorList = () => {
      window.localStorage.removeItem('SeniorListIndexStore');
      history.push('/seniorlist');
    };

    const clearAlumniList = () => {
      window.localStorage.removeItem('SeniorListIndexStore');
      history.push('/alumnilist');
    };

    const clearAftercareList = () => {
      window.localStorage.removeItem('SeniorListIndexStore');
      history.push('/aftercarelist');
    };

    const clearScholarshipList = () => {
      window.localStorage.removeItem('SeniorListIndexStore');
      history.push('/scholarshiplist');
    };

    const seniorList = (
      <button type='button' onClick={clearSeniorList} className={classes.dropdownLink} style={{ border: 'none', background: 'none' }}>
        <ActivityIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>High School Senior List</span>
      </button>
    );

    const alumniList = (
      <button type='button' onClick={clearAlumniList} className={classes.dropdownLink} style={{ border: 'none', background: 'none' }}>
        <ActivityIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Alumni List</span>
      </button>
    );

    const aftercareList = (
      <button type='button' onClick={clearAftercareList} className={classes.dropdownLink} style={{ border: 'none', background: 'none' }}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Aftercare Applications List</span>
      </button>
    );

    const scholarshipList = (
      <button type='button' onClick={clearScholarshipList} className={classes.dropdownLink} style={{ border: 'none', background: 'none' }}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Scholarship Applications List</span>
      </button>
    );

    const helprequestList = (
      <a href='/helprequestlist' className={classes.dropdownLink}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Help Request List</span>
      </a>
    );

    const checkinsList = (
      <a href='/contactlist' className={classes.dropdownLink}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Proactive Check-ins List</span>
      </a>
    );

    const reportMenu = (
      <a href='/reports' className={classes.dropdownLink}>
        <DescriptionIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Reports</span>
      </a>
    );

    const fileHostingMenuItem = (
      <a href={PATH.FILE_HOSTING} className={classes.dropdownLink}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>{MENU.FILE_HOSTING}</span>
      </a>
    );

    const staffAdminMenu = (
      <a href='/staffadmin' className={classes.dropdownLink}>
        <DescriptionIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Manage Staff Users</span>
      </a>
    );

    const impersonateUserMenu = (
      <button
        type='button'
        onClick={() => setIsImpersonationDialogOpen(true)}
        className={classes.dropdownLink}
        style={{ border: 'none', background: 'none' }}
      >
        <PeopleIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Impersonate User</span>
      </button>
    );

    const awardPaymentRequest = (
      <a href={PATH.AWARD_PAYMENT_REQUESTS} className={classes.dropdownLink}>
        <ListIcon className={classes.dropdownIcons} />
        <span style={{ marginLeft: 6, marginTop: 4 }}>Award Payment Request</span>
      </a>
    );

    const showStaffAdminMenuItem = authStore ? showFeature(featureSwitches, 'Manage Staff Users', null, authStore?.user?.result?.id) : false;
    const impersonationMenuItem = authStore?.isImpersonating ? null : canImpersonateUser ? impersonateUserMenu : null;
    const showAwardPaymentRequestMenuItem = authStore
      ? showFeature(featureSwitches, FEATURE_SWITCH.AWARD_PAYMENT, null, authStore?.user?.result?.id) &&
        isScholarshipCoordinator(authStore.roles, authStore?.user?.result?.position_title)
      : false;

    const menuItems = isAuthenticated
      ? isStudent || isAlumni
        ? [...(impersonationMenuItem ? [impersonationMenuItem] : []), <SignInSignOutItem authStore={authStore} seniorStore={seniorStore} />]
        : isFHT
        ? [<SignInSignOutItem authStore={authStore} seniorStore={seniorStore} />]
        : [
            seniorList,
            alumniList,
            aftercareList,
            scholarshipList,
            helprequestList,
            checkinsList,
            reportMenu,
            ...(isFileHostingAdminUser ? [fileHostingMenuItem] : []),
            ...(isAdminUser && showStaffAdminMenuItem ? [staffAdminMenu] : []),
            ...(impersonationMenuItem ? [impersonationMenuItem] : []),
            ...(showAwardPaymentRequestMenuItem ? [awardPaymentRequest] : []),
            <SignInSignOutItem authStore={authStore} seniorStore={seniorStore} />,
          ]
      : [<SignInSignOutItem authStore={authStore} seniorStore={seniorStore} />];

    return (
      <>
        <PleaseWait isLoading={isLoading} />
        <StartImpersonationDialog open={isImpersonationDialogOpen} onClose={() => setIsImpersonationDialogOpen(false)} authStore={authStore} />
        {isAuthenticated && authStore.notAuthorized === false && (
          <List className={classes.list + ' ' + classes.mlAuto}>
            <ListItem className={classes.listItem}>
              <Hidden mdDown implementation='css'>
                <CustomDropdown
                  noLiPadding
                  navDropdown
                  hoverColor={dropdownHoverColor}
                  buttonText='Menu'
                  buttonProps={{
                    className: classes.navLink,
                    color: 'transparent',
                  }}
                  buttonIcon={MenuIcon}
                  dropdownList={menuItems.map((item, index) => ({
                    ...item,
                    key: `menu-item-${index}`,
                  }))}
                />
              </Hidden>
              <Hidden lgUp>
                {menuItems.map((item, index) => (
                  <React.Fragment key={`menu-item-mobile-${index}`}>{item}</React.Fragment>
                ))}
              </Hidden>
            </ListItem>
          </List>
        )}
      </>
    );
  })
);

export default MenuItems;
